import { useCallback } from "react";
import { useDispatch } from "react-redux";
import useTokenHook from "../../hooks/context/useTokenHook";
import useUser from "../../hooks/context/userHook";
import useBoardAndItems from "../../hooks/context/useBoardAndItems";
import { useList } from "../../hooks/ui";
import { AppDispatch } from "../../redux/store/store";
import { setAddItemSuccess } from "../../redux/slices/addItemSuccessSlice";
import { setBoardName } from "../../redux/slices/onboardingTargetBoardNameSlice";
import { addNewItem } from "./AddItemUtils";
import logger from "../../lib/logger";

export interface AddItemToGroupOptions {
  boardId: string;
  boardName: string;
  processSubitems: boolean;
  processUpdates: boolean;
  batchSize?: number;
  onGroupSelect?: () => void;
}

/** Shares the selected items into a group of the target board. */
export function useAddItemToGroup({
  boardId,
  boardName,
  processSubitems,
  processUpdates,
  batchSize,
  onGroupSelect,
}: AddItemToGroupOptions) {
  const { userId, accountId } = useUser();
  const { currentBoard, itemIds } = useBoardAndItems();
  const { token } = useTokenHook();
  const dispatch = useDispatch<AppDispatch>();
  const { setItemLoading, setItemDisabled, removeItemDisabled, removeItemLoading } = useList();

  return useCallback(
    async (groupId: string) => {
      onGroupSelect?.();
      dispatch(setBoardName(boardName));
      dispatch(setAddItemSuccess(true));

      setItemLoading(`${groupId}-${boardId}`);
      setItemDisabled(`${groupId}-${boardId}`);

      try {
        await addNewItem({
          userId: String(userId),
          accountId: String(accountId),
          currentBoard: currentBoard!.toString(),
          itemIds: itemIds!,
          token: token!,
          processSubitems,
          processUpdates,
          batchSize,
          targetGroupId: groupId,
          targetBoardId: boardId,
          removeItemDisabled,
          removeItemLoading,
        });
      } catch (error) {
        // addNewItem has already released the group and logged the cause. Swallowing it here
        // keeps a failed share from surfacing as an unhandled rejection out of the click.
        logger.error("Error adding new item to board", { error });
      }
    },
    [
      boardId,
      boardName,
      currentBoard,
      itemIds,
      userId,
      accountId,
      token,
      processSubitems,
      processUpdates,
      batchSize,
      dispatch,
      setItemLoading,
      setItemDisabled,
      removeItemDisabled,
      removeItemLoading,
      onGroupSelect,
    ],
  );
}
